import { CuratorLevel, UserType } from "./admin";
import { baseClient, getUserId } from "./baseRequest";

export type Author = {
  name: string;
  id: string;
  password?: string;
  photoUrl?: string;
  description?: string;
  type: UserType;
  country?: string;
  email: string;
  role?: string;
  // GitHub username, maps PR reviews back to this team member.
  githubLogin?: string;
  createdAt: Date;
  state?: string;
  updatedAt?: Date;
  featured?: boolean;
  curatorLevel?: CuratorLevel | null;
  socialMedia?: Array<SocialMedia>;
  tag?: string;
  clock?: string;
  clocks?: Array<{ id: string }>;
  studioContract: {
    accepted: boolean;
    signedTimestamp: number | null;
  };
  manualPayout?: boolean;
  animated?: boolean;
  metadata?: {
    collaboratorId?: string;
    defaultPlanId?: string | null;
    isArtistPageHidden?: boolean;
    ignoreAffiliatePayments?: boolean;
  };
  klydoCount?: number;
};

export type SocialMedia = {
  link: string;
  network: string;
};

export type AuthorApiClient = {
  getAllAuthors: () => Promise<{ authors: Author[] }>;
  createAuthor: (author: Author) => Promise<{ author: Author }>;
  removeAuthor: (uid: string, role: string) => Promise<void>;
  updateAuthorRole: (uid: string, role: string) => Promise<void>;
  disconnectAuthorClock: () => Promise<void>;
  updateAuthor: (author: Partial<Author>) => Promise<void>;
  getAllUsers: (filters?: {
    manualPayout?: boolean;
    hasClock?: boolean;
    hasCollection?: boolean;
  }) => Promise<Author[]>;
  toggleFeatured: (uid: string) => Promise<void>;
};

const authorApiClient: AuthorApiClient = {
  getAllAuthors: async () => {
    return baseClient.get<{ authors: Author[] }>({
      url: `/authors/get-all`,
    });
  },

  createAuthor: async (author: Author) => {
    return baseClient.post<{ author: Author }>({
      url: `/authors/create-author`,
      body: author,
    });
  },

  removeAuthor: async (uid: string, role: string) => {
    return baseClient.delete<void>({
      url: `/authors/${uid}/remove-author`,
      params: { role },
    });
  },

  updateAuthorRole: async (uid: string, role: string) => {
    return baseClient.put<void>({
      url: `/authors/${uid}/update-author-role`,
      body: { role },
    });
  },

  disconnectAuthorClock: async () => {
    return baseClient.put<void>({
      url: `/authors/${getUserId}/disconnect-author-clock`,
    });
  },

  updateAuthor: async (author: Partial<Author>) => {
    return baseClient.put<void>({
      url: `/authors/${getUserId}/update-author`,
      body: { author },
    });
  },
  getAllUsers: async (filters?: {
    manualPayout?: boolean;
    hasClock?: boolean;
    hasCollection?: boolean;
  }) => {
    return baseClient.get<Author[]>({
      url: `/admin/users`,
      params: {
        populated: true,
        ...(filters?.manualPayout && { manualPayout: true }),
        ...(filters?.hasClock && { hasClock: true }),
        ...(filters?.hasCollection && { hasCollection: true }),
      },
    });
  },
  toggleFeatured: async (uid: string) => {
    return baseClient.post<void>({
      url: `/admin/users/${uid}/toggle-featured`,
    });
  },
};

export default authorApiClient;
